// frontend/src/super-admin/TenantDetailPage.tsx
//
// Single-tenant detail view for the platform admin. Wired to
// /api/admin/tenants/:id/detail.

import * as React from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Building2, Banknote, Users as UsersIcon, FileText,
  ShieldCheck, CalendarClock, Pause, Play, Plus, Save, Gift, UserCog,
} from 'lucide-react';
import { api, getToken } from '../core/api-client';
import { Button } from '../components/ui/button';
import { LoadingState } from '../design-system/components/LoadingState';
import { ErrorState } from '../design-system/components/ErrorState';
import { PageHeader } from '../design-system/components/PageHeader';
import { VISUAL, statusPill, type StatusTone } from '../design-system/visual';
import { useAdminPermission } from '../core/auth/useAdminPermission';
import { useAuth } from '../core/auth/AuthProvider';
import { stashAdminToken } from '../core/auth/impersonation';
import { useConfirm } from '../components/ui/confirm-dialog';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';

interface Tenant {
  id: string;
  slug: string;
  name: string;
  currency: string;
  status: string;
  trial_ends_at: string | null;
  suspended_at: string | null;
  created_at: string;
}

interface TenantSubscription {
  status: string;
  trial_end_at: string | null;
  current_period_start_at: string | null;
  current_period_end_at: string | null;
  active_employee_count: number | null;
  module_snapshot: Record<string, unknown> | null;
  /** Comp / free account (migration 043). */
  billing_exempt?: boolean | null;
  comp_reason?: string | null;
  comp_expires_at?: string | null;
}

/** Mirrors backend isCompActive (comp.ts): exempt + (no expiry OR future expiry). */
function compIsActive(sub: TenantSubscription | null): boolean {
  if (!sub || sub.billing_exempt !== true) return false;
  if (sub.comp_expires_at == null) return true;
  const exp = new Date(sub.comp_expires_at);
  if (Number.isNaN(exp.getTime())) return true;
  return exp.getTime() > Date.now();
}

interface TenantDetail {
  tenant: Tenant;
  subscription: TenantSubscription | null;
  modules: Array<{ module_key: string; enabled: boolean; enabled_at: string | null }>;
  counts: { employees: number; users: number; invoices: number };
  owner: { id: string; email: string; name: string; last_login_at: string | null } | null;
  /** All (up to 100) tenant users, active first — feeds the impersonation picker. */
  users?: Array<{ id: string; email: string; name: string; is_active: boolean }>;
}

interface ImpersonateResult {
  token: string;
  expires_at: string;
  user: { id: string; email: string; name: string };
  tenant: { id: string; slug: string; name: string };
}

// The 9 platform modules an admin can toggle for a tenant (mirrors the backend
// ALL_MODULE_KEYS in tenants.service.ts).
const ALL_MODULES = ['invoicing', 'payments', 'email', 'hr', 'payroll', 'monitoring', 'projects', 'expenses', 'esign'] as const;

export function TenantDetailPage() {
  const confirm = useConfirm();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { adoptToken } = useAuth();
  const canWrite = useAdminPermission('tenants.write');
  const canImpersonate = useAdminPermission('tenants.impersonate');
  const [detail, setDetail] = React.useState<TenantDetail | null>(null);
  const [err, setErr] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [impOpen, setImpOpen] = React.useState(false);
  const [impUserId, setImpUserId] = React.useState<string | null>(null);
  const [impBusy, setImpBusy] = React.useState(false);
  const [impErr, setImpErr] = React.useState<string | null>(null);
  const [extendDays, setExtendDays] = React.useState(7);
  // Draft set of enabled module keys, edited via checkboxes before Save.
  const [moduleDraft, setModuleDraft] = React.useState<Set<string>>(new Set());
  const [savingModules, setSavingModules] = React.useState(false);
  // Comp / free-account draft (migration 043).
  const [compReason, setCompReason] = React.useState('');
  const [compExpires, setCompExpires] = React.useState(''); // yyyy-mm-dd or ''
  const [savingComp, setSavingComp] = React.useState(false);

  function syncModuleDraft(modules: TenantDetail['modules']) {
    setModuleDraft(new Set(modules.filter((m) => m.enabled).map((m) => m.module_key)));
  }

  function load() {
    setErr(null);
    setDetail(null);
    api<TenantDetail>(`/api/admin/tenants/${id}/detail`)
      .then((d) => {
        setDetail(d);
        syncModuleDraft(d.modules);
        setCompReason(d.subscription?.comp_reason ?? '');
        setCompExpires(d.subscription?.comp_expires_at ? String(d.subscription.comp_expires_at).slice(0, 10) : '');
      })
      .catch((e) => setErr(String(e.message ?? e)));
  }
  React.useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  function toggleModule(key: string) {
    setModuleDraft((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  async function saveModules() {
    if (!detail || savingModules) return;
    setSavingModules(true);
    try {
      const r = await api<{ modules: TenantDetail['modules'] }>(
        `/api/admin/tenants/${id}/modules`,
        { method: 'PATCH', body: { enabled_module_keys: Array.from(moduleDraft) } },
      );
      setDetail({ ...detail, modules: r.modules });
      syncModuleDraft(r.modules);
      toast.success('Modules updated');
    } catch (e: any) {
      toast.error('Update failed', { description: e.message ?? String(e) });
    } finally { setSavingModules(false); }
  }

  async function suspend() {
    if (!detail || busy) return;
    // Copy corrected 2026-08-20: suspension does not block sign-in — it blocks
    // every module route with a 402 (module-gate.ts now reads tenants.status).
    // Billing and support surfaces stay reachable so they can resolve it, and
    // Reactivate restores the exact status suspension replaced.
    if (!(await confirm({
      title: `Suspend ${detail.tenant.name}?`,
      description: 'Everyone in this workspace immediately loses access to every module. Billing and support pages stay reachable, nothing is deleted, and Reactivate puts them back exactly as they were.',
      destructive: true,
      confirmLabel: 'Suspend',
    }))) return;
    setBusy(true);
    try {
      const r = await api<{ tenant: Tenant }>(`/api/admin/tenants/${id}/suspend`, { method: 'PATCH', body: {} });
      setDetail({ ...detail, tenant: r.tenant });
      toast.success('Workspace suspended');
    } catch (e: any) {
      toast.error('Suspend failed', { description: e.message ?? String(e) });
    } finally { setBusy(false); }
  }

  async function reactivate() {
    if (!detail || busy) return;
    setBusy(true);
    try {
      const r = await api<{ tenant: Tenant }>(`/api/admin/tenants/${id}/reactivate`, { method: 'PATCH', body: {} });
      setDetail({ ...detail, tenant: r.tenant });
      toast.success('Workspace re-activated');
    } catch (e: any) {
      toast.error('Re-activate failed', { description: e.message ?? String(e) });
    } finally { setBusy(false); }
  }

  async function extend() {
    if (!detail || busy || extendDays < 1) return;
    setBusy(true);
    try {
      const updated = await api<TenantDetail>(`/api/admin/tenants/${id}/extend-trial`, { method: 'POST', body: { days: extendDays } });
      setDetail(updated);
      toast.success(`Trial extended by ${extendDays} day${extendDays === 1 ? '' : 's'}`);
    } catch (e: any) {
      toast.error('Extend failed', { description: e.message ?? String(e) });
    } finally { setBusy(false); }
  }

  async function cancelSub() {
    if (!detail || busy) return;
    if (!(await confirm({ title: `Cancel ${detail.tenant.name}'s subscription?`, description: "They'll lose paid access at the next cycle.", destructive: true, confirmLabel: 'Cancel subscription' }))) return;
    setBusy(true);
    try {
      await api(`/api/admin/billing/subscriptions/${id}/cancel`, { method: 'POST', body: {} });
      load();
      toast.success('Subscription cancelled');
    } catch (e: any) {
      toast.error('Cancel failed', { description: e.message ?? String(e) });
    } finally { setBusy(false); }
  }

  async function setComp(exempt: boolean) {
    if (!detail || savingComp) return;
    setSavingComp(true);
    try {
      const body: { billing_exempt: boolean; comp_reason?: string | null; comp_expires_at?: string | null } = {
        billing_exempt: exempt,
      };
      if (exempt) {
        body.comp_reason = compReason.trim() || null;
        // Interpret the yyyy-mm-dd date as end-of-day UTC so the comp covers the whole day.
        body.comp_expires_at = compExpires ? new Date(`${compExpires}T23:59:59Z`).toISOString() : null;
      }
      const r = await api<{ subscription: TenantSubscription }>(
        `/api/admin/tenants/${id}/comp`,
        { method: 'PATCH', body },
      );
      setDetail({ ...detail, subscription: { ...(detail.subscription as TenantSubscription), ...r.subscription } });
      setCompReason(r.subscription.comp_reason ?? '');
      setCompExpires(r.subscription.comp_expires_at ? String(r.subscription.comp_expires_at).slice(0, 10) : '');
      toast.success(exempt ? 'Tenant marked as comped (free)' : 'Comp removed — normal billing resumes');
    } catch (e: any) {
      toast.error('Update failed', { description: e.message ?? String(e) });
    } finally { setSavingComp(false); }
  }

  // Impersonation picker pool: active tenant users from the detail payload's
  // `users` list, falling back to the owner alone if that list is somehow
  // empty (e.g. deploy skew against an older API build).
  const activeUsers = (detail?.users ?? []).filter((u) => u.is_active);
  const impersonatableUsers = activeUsers.length > 0
    ? activeUsers
    : (detail?.owner ? [{ id: detail.owner.id, email: detail.owner.email, name: detail.owner.name, is_active: true }] : []);

  function openImpersonate() {
    setImpErr(null);
    setImpUserId(impersonatableUsers[0]?.id ?? null);
    setImpOpen(true);
  }

  async function startImpersonate() {
    if (!impUserId || impBusy) return;
    setImpBusy(true);
    setImpErr(null);
    try {
      const result = await api<ImpersonateResult>(`/api/admin/tenants/${id}/impersonate`, {
        method: 'POST',
        body: { user_id: impUserId },
      });
      const adminToken = getToken();
      if (adminToken) stashAdminToken(adminToken);
      await adoptToken(result.token);
      setImpOpen(false);
      navigate('/');
    } catch (e: any) {
      setImpErr(e.message ?? String(e));
    } finally {
      setImpBusy(false);
    }
  }

  if (err)    return <ErrorState description={err} onRetry={load} />;
  if (!detail) return <LoadingState variant="page" />;

  const t = detail.tenant;
  const sub = detail.subscription;
  const comped = compIsActive(sub);
  const statusTone: StatusTone =
    t.status === 'active'    ? 'success' :
    t.status === 'trial'     ? 'info' :
    t.status === 'suspended' ? 'warning' :
    t.status === 'cancelled' ? 'destructive' : 'muted';

  return (
    <div>
      <PageHeader
        eyebrow={
          <>
            <Link to="/admin/tenants" className="hover:text-foreground transition-colors">Tenants</Link>
            {' / '}
            <span className="font-mono">{t.slug}</span>
          </>
        }
        title={t.name}
        description={`Created ${new Date(t.created_at).toLocaleDateString()} · ${t.currency}`}
        chip={<span className={statusPill(statusTone)}>{t.status}</span>}
        actions={
          <Link to="/admin/tenants">
            <Button variant="ghost" className="rounded-full h-10 px-3 text-[13px] gap-1.5 text-muted-foreground">
              <ArrowLeft className="h-3.5 w-3.5" /> Back
            </Button>
          </Link>
        }
      />

      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6 max-w-6xl">
        {/* KPI strip */}
        <section className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi icon={<UsersIcon className="h-4 w-4" />} label="Users" value={detail.counts.users} />
          <Kpi icon={<UsersIcon className="h-4 w-4" />} label="Employees" value={detail.counts.employees} />
          <Kpi icon={<FileText className="h-4 w-4" />} label="Invoices" value={detail.counts.invoices} />
          <Kpi icon={<Banknote className="h-4 w-4" />} label="Seats billed" value={sub?.active_employee_count ?? 0} />
        </section>

        {/* Subscription + actions */}
        <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
          <div className="flex items-center gap-3 mb-5">
            <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-coral/15 ring-1 ring-coral/25">
              <ShieldCheck className="h-4 w-4 text-coral" strokeWidth={1.7} />
            </div>
            <h3 className={VISUAL.dashSection}>Subscription</h3>
          </div>

          {sub ? (
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-[13.5px]">
              <Row label="Status">
                <span className="inline-flex items-center gap-2 flex-wrap">
                  <span className={statusPill(
                    sub.status === 'active'  ? 'success' :
                    sub.status === 'trial'   ? 'info' :
                    sub.status === 'past_due' || sub.status === 'read_only' ? 'warning' :
                    'muted',
                  )}>{sub.status}</span>
                  {comped && <span className={statusPill('success')}>Comped · Free</span>}
                </span>
              </Row>
              <Row label="Trial ends">
                <span className="font-mono tabular-nums">{sub.trial_end_at ? new Date(sub.trial_end_at).toLocaleDateString() : '—'}</span>
              </Row>
              <Row label="Current period">
                <span className="font-mono tabular-nums">
                  {sub.current_period_start_at ? new Date(sub.current_period_start_at).toLocaleDateString() : '—'}
                  {' – '}
                  {sub.current_period_end_at ? new Date(sub.current_period_end_at).toLocaleDateString() : '—'}
                </span>
              </Row>
              <Row label="Billed seats">
                <span className="font-mono tabular-nums">{sub.active_employee_count ?? 0}</span>
              </Row>
            </dl>
          ) : (
            <p className="text-[13px] text-muted-foreground">No subscription row yet.</p>
          )}

          {canWrite && (
            <div className="mt-6 flex flex-wrap gap-2">
              {t.status === 'suspended' ? (
                <Button onClick={reactivate} disabled={busy} className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5">
                  <Play className="h-3.5 w-3.5" /> Re-activate
                </Button>
              ) : (
                <Button onClick={suspend} disabled={busy} variant="outline" className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5">
                  <Pause className="h-3.5 w-3.5" /> Suspend
                </Button>
              )}

              <div className="inline-flex items-center gap-2 bg-muted/40 rounded-full pl-3 pr-1 py-1">
                <CalendarClock className="h-3.5 w-3.5 text-muted-foreground" />
                <span className="text-[12.5px] text-muted-foreground">Extend trial by</span>
                <input
                  type="number"
                  min={1}
                  max={365}
                  value={extendDays}
                  onChange={(e) => setExtendDays(Number(e.target.value) || 1)}
                  className="w-14 h-8 text-center rounded-full bg-background ring-1 ring-border/60 text-[12.5px] font-semibold tabular-nums focus:outline-none"
                />
                <Button onClick={extend} disabled={busy} variant="ghost" className="rounded-full h-8 px-3 text-[12.5px] font-semibold">
                  Apply
                </Button>
              </div>

              {sub && sub.status !== 'cancelled' && (
                <Button onClick={cancelSub} disabled={busy} variant="ghost" className="rounded-full h-10 px-4 text-[13px] font-semibold text-rose-700 hover:text-rose-800">
                  Cancel subscription
                </Button>
              )}
            </div>
          )}
        </section>

        {/* Comp / free account */}
        <CompCard
          canWrite={canWrite}
          active={comped}
          exemptFlag={sub?.billing_exempt === true}
          expiresAt={sub?.comp_expires_at ?? null}
          reason={compReason}
          onReason={setCompReason}
          expiresDraft={compExpires}
          onExpires={setCompExpires}
          saving={savingComp}
          onSet={setComp}
        />

        {/* Owner */}
        <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
          <div className="flex items-center justify-between gap-3 mb-5">
            <div className="flex items-center gap-3">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-lavender-100 ring-1 ring-lavender-200">
                <Building2 className="h-4 w-4 text-lavender-700" strokeWidth={1.7} />
              </div>
              <h3 className={VISUAL.dashSection}>Workspace owner</h3>
            </div>
            {canImpersonate && impersonatableUsers.length > 0 && (
              <Button
                onClick={openImpersonate}
                variant="outline"
                className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5"
              >
                <UserCog className="h-3.5 w-3.5" /> Impersonate
              </Button>
            )}
          </div>
          {detail.owner ? (
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-full bg-coral text-white flex items-center justify-center text-[16px] font-bold ring-1 ring-coral/30">
                {detail.owner.name.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0">
                <p className="text-[14.5px] font-semibold text-foreground truncate">{detail.owner.name}</p>
                <p className="text-[12.5px] text-muted-foreground truncate">{detail.owner.email}</p>
                {detail.owner.last_login_at && (
                  <p className="text-[11.5px] text-muted-foreground mt-0.5 tabular-nums">
                    Last sign-in {new Date(detail.owner.last_login_at).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-[13px] text-muted-foreground">No owner user found for this workspace.</p>
          )}
        </section>

        {/* Modules */}
        <ModulesSection
          canWrite={canWrite}
          draft={moduleDraft}
          onToggle={toggleModule}
          onSave={saveModules}
          saving={savingModules}
        />
      </div>

      <Dialog open={impOpen} onOpenChange={(open) => { if (!open && !impBusy) setImpOpen(false); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Impersonate a user</DialogTitle>
            <DialogDescription>
              You'll be signed in as this user in {t.name}. Your admin session is preserved and you can exit
              impersonation to return to it.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-2">
            {impersonatableUsers.map((u) => (
              <label
                key={u.id}
                className="flex items-center gap-3 px-4 py-3 rounded-2xl ring-1 ring-border/40 cursor-pointer"
              >
                <input
                  type="radio"
                  name="impersonate-user"
                  checked={impUserId === u.id}
                  onChange={() => setImpUserId(u.id)}
                  className="h-4 w-4 text-coral focus:ring-coral/40"
                />
                <span className="min-w-0">
                  <span className="block text-[13px] font-semibold text-foreground truncate">{u.name}</span>
                  <span className="block text-[12px] text-muted-foreground truncate">{u.email}</span>
                </span>
              </label>
            ))}
          </div>

          {impErr && <p className="text-[12.5px] text-rose-700">{impErr}</p>}

          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setImpOpen(false)} disabled={impBusy}>
              Cancel
            </Button>
            <Button onClick={startImpersonate} disabled={impBusy || !impUserId}>
              {impBusy ? 'Impersonating…' : 'Impersonate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ModulesSection({
  canWrite, draft, onToggle, onSave, saving,
}: {
  canWrite: boolean;
  draft: Set<string>;
  onToggle: (key: string) => void;
  onSave: () => void;
  saving: boolean;
}) {
  return (
    <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-coral/15 ring-1 ring-coral/25">
            <Plus className="h-4 w-4 text-coral" strokeWidth={1.7} />
          </div>
          <h3 className={VISUAL.dashSection}>Modules</h3>
        </div>
        <span className="text-[11.5px] uppercase tracking-[0.14em] font-bold text-muted-foreground tabular-nums">
          {draft.size} of {ALL_MODULES.length}
        </span>
      </div>

      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
        {ALL_MODULES.map((key) => {
          const on = draft.has(key);
          const label = key.replace(/[-_]/g, ' ');
          if (!canWrite) {
            return (
              <li
                key={key}
                className={
                  'flex items-center justify-between gap-3 px-4 py-3 rounded-2xl ring-1 ' +
                  (on ? 'bg-lime-neon/20 ring-lime-700/20' : 'bg-muted/40 ring-border/40')
                }
              >
                <span className="text-[13px] font-semibold text-foreground capitalize truncate">{label}</span>
                <span className={'text-[10.5px] font-bold uppercase tracking-[0.10em] shrink-0 ' + (on ? 'text-lime-700' : 'text-muted-foreground')}>
                  {on ? 'on' : 'off'}
                </span>
              </li>
            );
          }
          return (
            <li key={key}>
              <label
                className={
                  'flex items-center justify-between gap-3 px-4 py-3 rounded-2xl ring-1 cursor-pointer transition-colors ' +
                  (on ? 'bg-lime-neon/20 ring-lime-700/20' : 'bg-muted/40 ring-border/40 hover:ring-border')
                }
              >
                <span className="flex items-center gap-2.5 min-w-0">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={() => onToggle(key)}
                    className="h-4 w-4 rounded border-border/60 text-coral focus:ring-coral/40 shrink-0"
                  />
                  <span className="text-[13px] font-semibold text-foreground capitalize truncate">{label}</span>
                </span>
                <span className={'text-[10.5px] font-bold uppercase tracking-[0.10em] shrink-0 ' + (on ? 'text-lime-700' : 'text-muted-foreground')}>
                  {on ? 'on' : 'off'}
                </span>
              </label>
            </li>
          );
        })}
      </ul>

      {canWrite && (
        <div className="mt-5 flex items-center gap-3">
          <Button onClick={onSave} disabled={saving} className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5">
            <Save className="h-3.5 w-3.5" /> {saving ? 'Saving…' : 'Save modules'}
          </Button>
          <p className="text-[11.5px] text-muted-foreground leading-snug">
            Enabling a module here is an admin override — it won't auto-seed that module's role permissions.
          </p>
        </div>
      )}
    </section>
  );
}

function CompCard({
  canWrite, active, exemptFlag, expiresAt, reason, onReason, expiresDraft, onExpires, saving, onSet,
}: {
  canWrite: boolean;
  active: boolean;
  exemptFlag: boolean;
  expiresAt: string | null;
  reason: string;
  onReason: (v: string) => void;
  expiresDraft: string;
  onExpires: (v: string) => void;
  saving: boolean;
  onSet: (exempt: boolean) => void;
}) {
  // exemptFlag but not active = the exemption is set but has EXPIRED (normal billing resumed).
  const expired = exemptFlag && !active;
  return (
    <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
      <div className="flex items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-3">
          <div className="inline-flex h-10 w-10 items-center justify-center rounded-2xl bg-lime-neon/25 ring-1 ring-lime-700/20">
            <Gift className="h-4 w-4 text-lime-700" strokeWidth={1.7} />
          </div>
          <h3 className={VISUAL.dashSection}>Comp · Free account</h3>
        </div>
        {active
          ? <span className={statusPill('success')}>Comped — never billed</span>
          : expired
          ? <span className={statusPill('warning')}>Comp expired</span>
          : <span className={statusPill('muted')}>Normal billing</span>}
      </div>

      <p className="text-[12.5px] text-muted-foreground leading-relaxed mb-4">
        A comped tenant keeps full product access but is never invoiced, dunned, marked past-due, or suspended by billing.
        Leave the expiry blank for a permanent free account; an expired comp automatically reverts to normal billing.
      </p>

      {active && (
        <div className="mb-4 text-[13px] text-foreground">
          {reason && <p><span className="text-muted-foreground">Reason: </span>{reason}</p>}
          <p>
            <span className="text-muted-foreground">Expires: </span>
            <span className="font-mono tabular-nums">{expiresAt ? new Date(expiresAt).toLocaleDateString() : 'never'}</span>
          </p>
        </div>
      )}

      {canWrite ? (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block">
              <span className="text-[11.5px] uppercase tracking-[0.14em] font-bold text-muted-foreground">Reason (optional)</span>
              <input
                type="text"
                value={reason}
                onChange={(e) => onReason(e.target.value)}
                placeholder="e.g. Design partner, comped Q3"
                className="mt-1 w-full h-10 px-3 rounded-xl bg-background ring-1 ring-border/60 text-[13px] focus:outline-none focus:ring-coral/40"
              />
            </label>
            <label className="block">
              <span className="text-[11.5px] uppercase tracking-[0.14em] font-bold text-muted-foreground">Expires (blank = never)</span>
              <input
                type="date"
                value={expiresDraft}
                onChange={(e) => onExpires(e.target.value)}
                className="mt-1 w-full h-10 px-3 rounded-xl bg-background ring-1 ring-border/60 text-[13px] tabular-nums focus:outline-none focus:ring-coral/40"
              />
            </label>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => onSet(true)} disabled={saving} className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5">
              <Gift className="h-3.5 w-3.5" /> {active ? 'Update comp' : 'Mark as comped'}
            </Button>
            {exemptFlag && (
              <Button onClick={() => onSet(false)} disabled={saving} variant="ghost" className="rounded-full h-10 px-4 text-[13px] font-semibold text-rose-700 hover:text-rose-800">
                Remove comp
              </Button>
            )}
          </div>
        </div>
      ) : (
        <p className="text-[12.5px] text-muted-foreground">You need tenants.write to change comp status.</p>
      )}
    </section>
  );
}

function Kpi({ icon, label, value }: { icon: React.ReactNode; label: string; value: number | string }) {
  return (
    <div className={[VISUAL.bento, 'p-5'].join(' ')}>
      <div className="flex items-center gap-2.5 mb-2">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-2xl bg-muted/60 text-muted-foreground">{icon}</span>
        <p className={VISUAL.eyebrow}>{label}</p>
      </div>
      <p className="font-sans font-light text-[2.2rem] leading-none tracking-[-0.03em] tabular-nums text-foreground">{value}</p>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[11.5px] uppercase tracking-[0.14em] font-bold text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-foreground">{children}</dd>
    </div>
  );
}
